import { type Context, SpanStatusCode, trace } from '@opentelemetry/api'

import type { Schemas } from '~/clients/api-client'
import { runInChildSpan } from '~/utils/observability'

const MAX_ATTEMPTS = 3
const INITIAL_DELAY_MS = 500

// Missing and null values are evidence too; validation and quarantine belong to the backend.
export type PlanningNoteObservation = Readonly<Schemas['RawDipsPlanningNoteObservationRequest']>
export type PlanningNoteCaptureBatchOutcome = 'ok' | 'failed-http' | 'failed-unhandled' | 'bailed'

function isRetryable(status: number): boolean {
    return status === 408 || status === 425 || status === 429 || (status >= 500 && status <= 599)
}

function isNetworkError(error: unknown): boolean {
    return error instanceof TypeError || (error instanceof Error && error.name === 'NetworkError')
}

function exponentialJitterDelayMs(attempt: number): number {
    return Math.floor(Math.random() * INITIAL_DELAY_MS * 2 ** (attempt - 1))
}

function wait(delay: number, signal: AbortSignal): Promise<boolean> {
    return new Promise(resolve => {
        const timer: { current?: ReturnType<typeof setTimeout> } = {}
        const onAbort = () => {
            clearTimeout(timer.current)
            signal.removeEventListener('abort', onAbort)
            resolve(false)
        }
        timer.current = setTimeout(() => {
            signal.removeEventListener('abort', onAbort)
            resolve(true)
        }, delay)
        signal.addEventListener('abort', onAbort, { once: true })
        if (signal.aborted) onAbort()
    })
}

async function discardUnreadBody(response: Response): Promise<void> {
    if (!response.body || response.bodyUsed) return
    try {
        await response.body.cancel()
    } catch {
        // Resource cleanup must not change the request outcome.
    }
}

/**
 * Owns one background planning-note capture batch: request execution, transient
 * retries and backoff, cancellation, response cleanup, failure classification,
 * and safe telemetry. This keeps transport policy out of queue orchestration.
 * Generalize it if another background sender needs the same policy.
 */
export function sendPlanningNoteCaptureBatch(
    batch: PlanningNoteObservation[],
    send: (batch: PlanningNoteObservation[], signal: AbortSignal) => Promise<{ response: Response }>,
    { signal, isEnabled }: { signal: AbortSignal; isEnabled: () => boolean },
    parentContext: Context
): Promise<PlanningNoteCaptureBatchOutcome> {
    return runInChildSpan(
        'raw-dips-planning-note.capture.send_batch',
        async spanContext => {
            const span = trace.getSpan(spanContext)
            let attempts = 0
            const shouldBail = () => signal.aborted || !isEnabled()
            span?.setAttribute('app.retry.max_attempts', MAX_ATTEMPTS)

            const finish = (outcome: PlanningNoteCaptureBatchOutcome) => {
                span?.setAttribute('app.retry.outcome', outcome)
                span?.setAttribute('app.retry.attempt_count', attempts)
                span?.setAttribute('app.retry.retry_count', Math.max(0, attempts - 1))
                if (outcome !== 'ok' && outcome !== 'bailed') span?.setStatus({ code: SpanStatusCode.ERROR })
                else if (outcome === 'ok') span?.setStatus({ code: SpanStatusCode.OK })
                return outcome
            }

            while (attempts < MAX_ATTEMPTS) {
                if (shouldBail()) return finish('bailed')
                attempts++

                let response: Response | undefined
                try {
                    response = (await send(batch, signal)).response
                } catch (error) {
                    if (shouldBail()) return finish('bailed')
                    if (!isNetworkError(error)) return finish('failed-unhandled')
                }

                let status: number | undefined
                try {
                    if (shouldBail()) return finish('bailed')
                    if (response?.ok) return finish('ok')

                    status = response?.status
                    if (status !== undefined && !isRetryable(status)) return finish('failed-http')
                    if (attempts === MAX_ATTEMPTS) return finish(status === undefined ? 'failed-unhandled' : 'failed-http')
                } finally {
                    if (response) await discardUnreadBody(response)
                }

                // Follow-up: Honor Retry-After if the capture endpoint starts returning it.
                const delay = exponentialJitterDelayMs(attempts)
                span?.addEvent('app.retry.scheduled', {
                    'app.retry.attempt': attempts + 1,
                    'app.retry.delay_ms': delay,
                    'app.retry.reason': response ? 'http' : 'network_error',
                    ...(status === undefined ? {} : { 'http.response.status_code': status }),
                })

                if (!(await wait(delay, signal))) return finish('bailed')
            }

            return finish('failed-unhandled')
        },
        parentContext
    )
}
